import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  ArrowUp,
  Plus,
  Trash2,
  History,
  Camera,
  Paperclip,
  X,
  FilePlus2,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { useAuth } from "@/context/AuthContext";
import { API, api, errMsg, refreshSession } from "@/lib/api";
import {
  DraftCard,
  ChoicesCard,
  CancelCard,
  ConfirmedCard,
  ToBuyCard,
  SupplierCard,
  SourcesCard,
  StockChangeCard,
  StockChangeConfirmedCard,
  InventoryResultsCard,
} from "@/components/chat/ChatCards";
import {
  ScreenshotPracticalDialog,
  PracticalCreatedCard,
} from "@/components/ScreenshotPracticalDialog";
import { TodayBookingsSummary } from "@/components/TodayBookingsSummary";
import { usePageGuidance } from "@/components/common";

const PROMPTS = {
  teacher: [
    "Book an osmosis practical for my next Year 8 lesson",
    "What's my next lesson?",
    "Have I booked anything for Friday?",
    "Where are the microscopes stored?",
  ],
  technician: [
    "What practicals are booked for tomorrow?",
    "Which practicals next week need microscopes?",
    "5 microscopes are broken",
    "Any pending requests?",
  ],
};

const greetName = (name) => {
  const parts = name
    .replace(/\(.*?\)/g, "")
    .trim()
    .split(/\s+/);
  return /^(Mr|Mrs|Ms|Miss|Dr|Prof)\.?$/i.test(parts[0]) && parts.length > 1
    ? parts[1]
    : parts[0];
};

const timeGreeting = () => {
  const h = new Date().getHours();
  return h < 12 ? "Morning" : h < 18 ? "Afternoon" : "Evening";
};

function JonnyAvatar({ src, large }) {
  const size = large ? "h-[52px] w-[52px]" : "h-8 w-8";
  return (
    <img
      src={src || "/askjonnylogosmall.png"}
      alt="Jonny"
      className={`${size} rounded-full object-cover shrink-0 bg-white shadow-sm`}
      data-testid="jonny-avatar"
    />
  );
}

function ConversationList({ items, active, onOpen, onNew, onDelete }) {
  return (
    <div className="flex flex-col h-full">
      <div className="p-4">
        <Button
          onClick={onNew}
          className="w-full rounded-full h-10 font-semibold shadow-sm"
          data-testid="new-conversation-button"
        >
          <Plus className="h-4 w-4 mr-2" />
          New chat
        </Button>
      </div>
      <div className="px-5 pt-2 pb-2 text-[10px] tracking-[0.2em] uppercase text-muted-foreground">
        Recent
      </div>
      <div
        className="flex-1 overflow-y-auto px-3 pb-3 space-y-0.5"
        data-testid="conversation-list"
      >
        {items.length === 0 && (
          <p
            className="px-2 text-sm text-muted-foreground"
            data-testid="conversation-list-empty"
          >
            No conversations yet.
          </p>
        )}
        {items.map((c) => (
          <div
            key={c.id}
            className={`group flex items-center rounded-lg ${c.id === active ? "bg-muted" : "hover:bg-muted/60"}`}
          >
            <button
              onClick={() => onOpen(c.id)}
              className="flex-1 text-left text-sm px-3 py-2 truncate"
              data-testid={`conversation-item-${c.id}`}
            >
              {c.title}
            </button>
            <button
              onClick={() => onDelete(c.id)}
              className="opacity-0 group-hover:opacity-100 p-2 text-muted-foreground hover:text-destructive"
              data-testid={`conversation-delete-${c.id}`}
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

function PracticalFormCard({ prefill, onOpen, disabled }) {
  return (
    <div
      className="space-y-2 rounded-xl border border-primary/30 bg-primary/5 p-4"
      data-testid="chat-practical-form-card"
    >
      <p className="text-sm font-semibold" data-testid="chat-practical-form-title">
        New practical form ready
      </p>
      <p className="text-xs text-muted-foreground" data-testid="chat-practical-form-summary">
        {prefill.name || "New practical"}{prefill.subject ? ` · ${prefill.subject}` : ""}
      </p>
      <Button
        size="sm"
        onClick={() => onOpen(prefill)}
        disabled={disabled}
        data-testid="chat-open-practical-form-button"
      >
        <FilePlus2 className="mr-1.5 h-3.5 w-3.5" />
        Open practical form
      </Button>
    </div>
  );
}

function Message({
  m,
  avatar,
  latestDraft,
  onPick,
  busy,
  cid,
  onConfirmed,
  onOpenPractical,
}) {
  if (m.role === "user") {
    return (
      <div className="flex justify-end rise">
        <div
          className="max-w-[85%] rounded-2xl rounded-br-md bg-primary text-primary-foreground px-4 py-2.5 text-sm whitespace-pre-wrap"
          data-testid="chat-user-message"
        >
          {m.image && (
            <img
              src={`${API}/chat/conversations/${cid}/images/${m.image.id}`}
              alt="Image shared with Jonny"
              className="mb-2 max-h-64 w-full rounded-lg bg-black/10 object-contain"
              data-testid={`chat-user-message-image-${m.image.id}`}
            />
          )}
          {m.content}
        </div>
      </div>
    );
  }
  return (
    <div
      className="flex gap-3 rise"
      data-testid={
        m.role === "system" ? "chat-system-message" : "chat-assistant-message"
      }
    >
      {m.role === "system" ? (
        <div className="w-8 shrink-0" />
      ) : (
        <JonnyAvatar src={avatar} />
      )}
      <div className="flex-1 min-w-0 space-y-3">
        {m.content && (
          <div
            className={`prose-jonny text-sm leading-relaxed ${m.error ? "text-destructive" : ""}`}
          >
            <ReactMarkdown remarkPlugins={[remarkGfm]}>
              {m.content}
            </ReactMarkdown>
          </div>
        )}
        {m.streaming && !m.content && (
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <span className="dot-pulse flex gap-1">
              <span />
              <span />
              <span />
            </span>
            {m.tool || "Thinking"}…
          </div>
        )}
        {m.streaming && m.content && m.tool && (
          <div className="text-xs text-muted-foreground">{m.tool}…</div>
        )}
        {(m.cards || []).map((c, i) => {
          if (c.type === "draft")
            return (
              <DraftCard
                key={`${c.draft.id}-${c.draft.version}`}
                draft={c.draft}
                isLatest={latestDraft[c.draft.id] === c.draft.version}
                onConfirmed={onConfirmed}
              />
            );
          if (c.type === "choices")
            return (
              <ChoicesCard key={i} card={c} onPick={onPick} disabled={busy} />
            );
          if (c.type === "cancel")
            return (
              <CancelCard key={i} booking={c.booking} conversationId={cid} />
            );
          if (c.type === "confirmed")
            return <ConfirmedCard key={i} booking={c.booking} />;
          if (c.type === "cancelled")
            return <ConfirmedCard key={i} booking={c.booking} cancelled />;
          if (c.type === "tobuy")
            return <ToBuyCard key={i} item={c.item} verb={c.verb} />;
          if (c.type === "supplier")
            return <SupplierCard key={i} entry={c.entry} verb={c.verb} />;
          if (c.type === "inventory") return <InventoryResultsCard key={i} items={c.items} />;
          if (c.type === "sources")
            return <SourcesCard key={i} documents={c.documents} />;
          if (c.type === "stock_change")
            return <StockChangeCard key={c.draft.id} draft={c.draft} />;
          if (c.type === "stock_change_confirmed")
            return <StockChangeConfirmedCard key={c.log.id} log={c.log} />;
          if (c.type === "practical_created")
            return (
              <PracticalCreatedCard
                key={i}
                practical={c.practical}
                onBook={onPick}
                disabled={busy}
              />
            );
          if (c.type === "practical_form") {
            return (
              <PracticalFormCard
                key={i}
                prefill={c.prefill}
                onOpen={onOpenPractical}
                disabled={busy}
              />
            );
          }
          return null;
        })}
      </div>
    </div>
  );
}

async function streamPost(url, body, onEvent) {
  const doFetch = () =>
    fetch(url, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
  let res = await doFetch();
  if (res.status === 401) {
    await refreshSession();
    res = await doFetch();
  }
  if (!res.ok)
    throw new Error(
      (await res.json().catch(() => ({}))).detail || "Request failed",
    );
  const reader = res.body.getReader();
  const dec = new TextDecoder();
  let buf = "";
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += dec.decode(value, { stream: true });
    const parts = buf.split("\n\n");
    buf = parts.pop();
    parts.forEach(
      (p) => p.startsWith("data: ") && onEvent(JSON.parse(p.slice(6))),
    );
  }
}

export default function ChatPage() {
  const { cid } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { guidance } = usePageGuidance();
  const [convs, setConvs] = useState([]);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [histOpen, setHistOpen] = useState(false);
  const [shotOpen, setShotOpen] = useState(false);
  const [starterQuestions, setStarterQuestions] = useState(null);
  const [attachedImage, setAttachedImage] = useState(null);
  const skipLoad = useRef(null);
  const endRef = useRef(null);
  const imagePreviewRef = useRef(null);
  const imageFileInputRef = useRef(null);
  const avatar = user.school?.branding?.avatar || "/askjonnylogosmall.png";
  const chatSubtitle = guidance?.subtitle || (
    <>
      {user.school?.week ? `It's Week ${user.school.week}. ` : ""}
      I can find practicals, book them against your real timetable, check prep status and
      look up equipment.
    </>
  );

  const clearAttachedImage = useCallback(() => {
    if (imagePreviewRef.current) {
      URL.revokeObjectURL(imagePreviewRef.current);
      imagePreviewRef.current = null;
    }
    setAttachedImage(null);
  }, []);
  const attachImage = useCallback((file) => {
    const supportedTypes = ["image/jpeg", "image/png", "image/webp"];
    if (!supportedTypes.includes(file?.type)) {
      toast.error("Choose a JPEG, PNG or WebP image.");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Images must be 5 MB or smaller.");
      return;
    }
    if (imagePreviewRef.current) {
      URL.revokeObjectURL(imagePreviewRef.current);
    }
    const preview = URL.createObjectURL(file);
    imagePreviewRef.current = preview;
    setAttachedImage({ file, preview });
  }, []);
  useEffect(() => () => {
    if (imagePreviewRef.current) {
      URL.revokeObjectURL(imagePreviewRef.current);
    }
  }, []);

  const loadConvs = useCallback(
    () =>
      api
        .get("/chat/conversations")
        .then((r) => setConvs(r.data))
        .catch(() => {}),
    [],
  );
  useEffect(() => {
    loadConvs();
  }, [loadConvs]);
  useEffect(() => {
    api
      .get("/chat/starter-questions")
      .then((response) => setStarterQuestions(response.data))
      .catch(() => setStarterQuestions(null));
  }, []);
  useEffect(() => {
    if (!cid) {
      setMessages([]);
      return;
    }
    if (skipLoad.current === cid) return;
    api
      .get(`/chat/conversations/${cid}`)
      .then((r) => setMessages(r.data.messages))
      .catch(() => navigate("/chat", { replace: true }));
  }, [cid, navigate]);
  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages]);

  const latestDraft = useMemo(() => {
    const m = {};
    messages.forEach((msg) =>
      (msg.cards || []).forEach((c) => {
        if (c.type === "draft")
          m[c.draft.id] = Math.max(m[c.draft.id] || 0, c.draft.version);
      }),
    );
    return m;
  }, [messages]);

  const patchLast = (fn) =>
    setMessages((ms) => ms.map((m, i) => (i === ms.length - 1 ? fn(m) : m)));

  const send = async (text) => {
    const t = (text ?? input).trim();
    const imageToSend = attachedImage?.file;
    if ((!t && !imageToSend) || busy) return;
    setInput("");
    setBusy(true);
    let id = cid;
    try {
      if (!id) {
        const { data } = await api.post("/chat/conversations");
        id = data.id;
        skipLoad.current = id;
        navigate(`/chat/${id}`, { replace: true });
      }
      let image = null;
      if (imageToSend) {
        const formData = new FormData();
        formData.append("file", imageToSend);
        const { data } = await api.post(`/chat/conversations/${id}/images`, formData);
        image = data;
        clearAttachedImage();
      }
      setMessages((ms) => [
        ...ms,
        { id: `u${Date.now()}`, role: "user", content: t, image },
        {
          id: "streaming",
          role: "assistant",
          content: "",
          cards: [],
          streaming: true,
        },
      ]);
      await streamPost(
        `${API}/chat/conversations/${id}/messages`,
        { text: t, image_id: image?.id || null },
        (ev) => {
          if (ev.type === "text")
            patchLast((m) => ({
              ...m,
              content: m.content + ev.content,
              tool: null,
            }));
          else if (ev.type === "tool")
            patchLast((m) => ({ ...m, tool: ev.label }));
          else if (ev.type === "card")
            patchLast((m) => ({ ...m, cards: [...m.cards, ev.card] }));
          else if (ev.type === "error")
            patchLast((m) => ({ ...m, content: ev.content, error: true }));
          else if (ev.type === "done") {
            patchLast(() => ev.message);
            const practicalForm = ev.message.cards?.find(
              (card) => card.type === "practical_form",
            );
            if (practicalForm) {
              openPracticalForm(practicalForm.prefill);
            }
          }
        },
      );
      loadConvs();
    } catch (e) {
      toast.error(errMsg(e));
      patchLast((m) =>
        m.streaming
          ? {
              ...m,
              streaming: false,
              error: true,
              content:
                "Sorry — I couldn't reach the server. Nothing was saved.",
            }
          : m,
      );
    } finally {
      setBusy(false);
    }
  };

  const onConfirmed = (b) =>
    setMessages((ms) => [
      ...ms,
      {
        id: `s${Date.now()}`,
        role: "system",
        content: "",
        cards: [{ type: "confirmed", booking: b }],
      },
    ]);
  const onShotSaved = (pr) =>
    setMessages((ms) => [
      ...ms,
      {
        id: `s${Date.now()}`,
        role: "system",
        content: "",
        cards: [{ type: "practical_created", practical: pr }],
      },
    ]);
  const openPracticalForm = useCallback((prefill) => {
    navigate("/practicals", { state: { practicalPrefill: prefill } });
  }, [navigate]);
  const del = async (id) => {
    await api.delete(`/chat/conversations/${id}`);
    loadConvs();
    if (id === cid) navigate("/chat");
  };
  const listProps = {
    items: convs,
    active: cid,
    onOpen: (id) => {
      skipLoad.current = null;
      setHistOpen(false);
      navigate(`/chat/${id}`);
    },
    onNew: () => {
      setHistOpen(false);
      navigate("/chat");
    },
    onDelete: del,
  };
  const promptRole = user.role === "technician" ? "technician" : "teacher";
  const prompts = starterQuestions?.[promptRole] || PROMPTS[promptRole];

  return (
    <div className="flex h-full min-h-0">
      <aside className="hidden xl:block w-72 border-r shrink-0 bg-background">
        <ConversationList {...listProps} />
      </aside>
      <Sheet open={histOpen} onOpenChange={setHistOpen}>
        <SheetContent side="left" className="p-0 w-72">
          <SheetTitle className="sr-only">Conversations</SheetTitle>
          <ConversationList {...listProps} />
        </SheetContent>
      </Sheet>
      <section className="flex-1 flex flex-col min-w-0 lab-lines">
        <div className="xl:hidden flex items-center justify-between px-4 h-10 border-b bg-background/80">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setHistOpen(true)}
            data-testid="conversation-history-button"
          >
            <History className="h-4 w-4 mr-1.5" />
            History
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate("/chat")}
            data-testid="header-new-chat-button"
          >
            <Plus className="h-4 w-4 mr-1" />
            New
          </Button>
        </div>
        <div className="flex-1 overflow-y-auto" data-testid="chat-messages">
          <div className="max-w-3xl mx-auto min-h-full flex flex-col px-4 sm:px-6">
            {messages.length === 0 && (
              <div
                className="pt-4 rise"
                data-testid="chat-empty-state"
              >
                <div className="flex items-start gap-4">
                  <JonnyAvatar src={avatar} large />
                  <h1
                    className="flex-1 font-display text-4xl sm:text-5xl font-bold leading-[1.08] tracking-tight"
                    data-testid="chat-greeting"
                  >
                    {timeGreeting()}, {greetName(user.name)}.{" "}
                    <span className="text-muted-foreground/90">
                      What do you need for the lab?
                    </span>
                  </h1>
                </div>
                <p
                  className="text-muted-foreground mt-4 text-base max-w-xl leading-relaxed"
                  data-testid="chat-intro"
                >
                  {chatSubtitle}
                </p>
                <TodayBookingsSummary />
                <div className="grid sm:grid-cols-2 gap-3 mt-10">
                  {prompts.map((p, i) => (
                    <button
                      key={p}
                      onClick={() => send(p)}
                      className="rise text-left text-sm rounded-xl border bg-card px-4 py-4 min-h-[60px] shadow-sm hover:border-primary/60 hover:-translate-y-0.5 transition-[transform,border-color]"
                      style={{ animationDelay: `${i * 60}ms` }}
                      data-testid={`suggested-prompt-${i}`}
                    >
                      {p}
                    </button>
                  ))}
                </div>
              </div>
            )}
            <div className={messages.length ? "space-y-6 py-6" : ""}>
              {messages.map((m) => (
                <Message
                  key={m.id}
                  m={m}
                  avatar={avatar}
                  latestDraft={latestDraft}
                  onPick={send}
                  busy={busy}
                  cid={cid}
                  onConfirmed={onConfirmed}
                  onOpenPractical={openPracticalForm}
                />
              ))}
            </div>
            <div ref={endRef} />
          </div>
        </div>
        <div className="px-4 pb-4 pt-2 bg-background/70 backdrop-blur-sm">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              send();
            }}
            className="max-w-3xl mx-auto rounded-2xl border bg-card px-2 py-2 shadow-sm focus-within:ring-2 focus-within:ring-ring/30"
          >
            {attachedImage && (
              <div
                className="relative mb-2 inline-flex rounded-lg border bg-muted/30 p-1"
                data-testid="chat-image-preview"
              >
                <img
                  src={attachedImage.preview}
                  alt="Image ready to send"
                  className="h-20 w-28 rounded-md object-cover"
                  data-testid="chat-image-preview-image"
                />
                <Button
                  type="button"
                  size="icon"
                  variant="secondary"
                  className="absolute -right-2 -top-2 h-6 w-6 rounded-full shadow-sm"
                  onClick={clearAttachedImage}
                  title="Remove image"
                  aria-label="Remove image"
                  data-testid="chat-image-preview-remove-button"
                >
                  <X className="h-3.5 w-3.5" />
                </Button>
              </div>
            )}
            <div className="flex items-center gap-2">
              <Textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                rows={1}
                placeholder="Ask Jonny about your lab..."
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    send();
                  }
                }}
                onPaste={(event) => {
                  const imageItem = Array.from(event.clipboardData?.items || []).find(
                    (item) => ["image/jpeg", "image/png", "image/webp"].includes(item.type),
                  );
                  if (imageItem) {
                    event.preventDefault();
                    const pastedImage = imageItem.getAsFile();
                    if (pastedImage) {
                      attachImage(pastedImage);
                    }
                  }
                }}
                className="min-h-[44px] max-h-40 resize-none border-0 py-3 text-[15px] shadow-none focus-visible:ring-0"
                data-testid="chat-input-textarea"
              />
              <input
                id="chat-image-file-input"
                ref={imageFileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                className="hidden"
                onChange={(event) => {
                  const selectedImage = event.target.files?.[0];
                  if (selectedImage) {
                    attachImage(selectedImage);
                  }
                  event.target.value = "";
                }}
                data-testid="chat-image-file-input"
              />
              <Button
                type="button"
                size="icon"
                variant="ghost"
                className="h-9 w-9 shrink-0 rounded-full"
                onClick={() => imageFileInputRef.current?.click()}
                title="Attach image"
                aria-label="Attach image"
                data-testid="chat-image-attach-button"
              >
                <Paperclip className="h-4 w-4" />
              </Button>
              <Button
                type="button"
                size="icon"
                variant="ghost"
                className="rounded-full h-9 w-9 shrink-0"
                onClick={() => setShotOpen(true)}
                title="Add a practical from a screenshot"
                aria-label="Add a practical from a screenshot"
                data-testid="chat-screenshot-button"
              >
                <Camera className="h-4 w-4" />
              </Button>
              <Button
                type="submit"
                size="icon"
                className="rounded-full h-9 w-9 shrink-0 disabled:opacity-40"
                disabled={busy || (!input.trim() && !attachedImage)}
                data-testid="chat-send-button"
              >
                <ArrowUp className="h-4 w-4" />
              </Button>
            </div>
          </form>
          <p
            className="max-w-3xl mx-auto text-[11px] text-muted-foreground mt-2 text-center"
            data-testid="chat-disclaimer"
          >
            Jonny only reports school records from the database. Always follow
            your school's risk assessment process.
          </p>
        </div>
      </section>
      <ScreenshotPracticalDialog
        open={shotOpen}
        onOpenChange={setShotOpen}
        onSaved={onShotSaved}
      />
    </div>
  );
}
